// Driver segregation: Food-delivery drivers vs Taxi drivers.
//
// - "delivery" = private vehicle, NO taxi permit → two-wheeler only → food delivery ONLY
//                (never offered taxi passenger requests).
// - "taxi"     = valid Commercial Taxi Permit → scooter / auto / car / jumbo → food delivery + taxi.
//
// Live enforcement (which requests a driver receives) belongs to the external backend —
// see /app/memory/DRIVER_SEGREGATION_BACKEND_BRIEF.md. This module persists the driver's
// choice to the backend profile AND mirrors it locally so the UI behaves correctly even
// before the backend stores the new fields.

export const VEHICLES = [
  { value: "scooter", label: "Scooter", twoWheeler: true },
  { value: "auto", label: "Autorickshaw", twoWheeler: false },
  { value: "car", label: "Taxi Car", twoWheeler: false },
  { value: "jumbo", label: "Jumbo Taxi", twoWheeler: false },
];
export const TWO_WHEELERS = ["scooter"];
export const TAXI_PERMIT_DOC = { doc_type: "taxi_permit", label: "Commercial Taxi Permit" };

// Vehicle categories chosen at signup → determine the role a driver gets.
export const VEHICLE_CATEGORIES = [
  { value: "private_scooter", label: "Private Scooter / Bike", vehicle: "scooter", commercial: false, role: "Food delivery only", desc: "Deliver food orders. No passenger rides, no permit needed." },
  { value: "commercial_scooter", label: "Commercial Scooter / Bike", vehicle: "scooter", commercial: true, role: "Food + Rides", desc: "Accept both food orders and customer rides." },
  { value: "commercial_auto", label: "Commercial Autorickshaw", vehicle: "auto", commercial: true, role: "City rides", desc: "Accept customer rides within the city." },
  { value: "commercial_car", label: "Commercial Taxi Car", vehicle: "car", commercial: true, role: "City + intercity rides", desc: "Accept city and intercity customer rides." },
  { value: "commercial_jumbo", label: "Commercial Jumbo Taxi", vehicle: "jumbo", commercial: true, role: "City + intercity rides", desc: "Accept city and intercity customer rides for larger groups." },
  { value: "private_car_4", label: "Private Car (4-seater)", vehicle: "private_car", commercial: false, role: "Private car hire", desc: "Luxury car hired per day, booked in advance. No permit; you set your daily rate." },
  { value: "private_car_6", label: "Private Car (6-seater)", vehicle: "private_car", commercial: false, role: "Private car hire", desc: "Luxury 6-seater hired per day, booked in advance. No permit; you set your daily rate." },
];
export const categoryInfo = (value) => VEHICLE_CATEGORIES.find((c) => c.value === value) || null;
export const isCommercialCategory = (value) => !!categoryInfo(value)?.commercial;
export const isPrivateCarCategory = (value) => value === "private_car_4" || value === "private_car_6";

// A driver's job capabilities, used to show only the relevant UI (nav, reports).
export function driverCaps(user) {
  const cat = categoryOf(user);
  const isPrivateCar = isPrivateCarCategory(cat);
  const canRides = !isPrivateCar && driverType(user) === "taxi";
  const canFood = cat === "private_scooter" || cat === "commercial_scooter";
  return { isPrivateCar, canRides, canFood };
}

// Resolve a driver's category, inferring it for legacy accounts.
export function categoryOf(user) {
  const local = loadDriverLocal();
  const c = user?.vehicle_category || local.vehicle_category;
  if (categoryInfo(c)) return c;
  const vt = user?.vehicle_type || local.vehicle_type;
  if (vt === "scooter") return driverType(user) === "taxi" ? "commercial_scooter" : "private_scooter";
  return { auto: "commercial_auto", car: "commercial_car", jumbo: "commercial_jumbo" }[vt] || null;
}

const LKEY = "kt_driver_profile";
const PERMIT_KEY = "kt_taxi_permit_uploaded";

export function loadDriverLocal() {
  try {
    return JSON.parse(localStorage.getItem(LKEY)) || {};
  } catch {
    return {};
  }
}
export function saveDriverLocal(patch) {
  const next = { ...loadDriverLocal(), ...patch };
  localStorage.setItem(LKEY, JSON.stringify(next));
  return next;
}

// Effective driver type. Falls back to services for legacy accounts (keeps old behaviour = taxi).
export function driverType(user) {
  const local = loadDriverLocal();
  const t = user?.driver_type || local.driver_type;
  if (t === "delivery" || t === "taxi") return t;
  const services = user?.services || local.services;
  if (Array.isArray(services) && services.length && !services.includes("ride")) return "delivery";
  return "taxi";
}
export const isDeliveryOnly = (user) => driverType(user) === "delivery";

export function permitInfo(user) {
  const local = loadDriverLocal();
  return {
    number: user?.taxi_permit_number || local.taxi_permit_number || "",
    expiry: user?.taxi_permit_expiry || local.taxi_permit_expiry || "",
  };
}

export const markPermitUploaded = () => localStorage.setItem(PERMIT_KEY, new Date().toISOString());
export const permitUploadedAt = () => localStorage.getItem(PERMIT_KEY) || null;

// Services allowed for a given driver type.
export function allowedServices(type) {
  return type === "delivery" ? ["food"] : ["ride", "food"];
}
// Vehicles selectable for a given driver type.
export function allowedVehicles(type) {
  return type === "delivery" ? VEHICLES.filter((v) => v.twoWheeler) : VEHICLES;
}
